import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import type { CompanyMemberRole } from '../types';

type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'assign' | 'close' | 'viewInternal' | 'manage';
type ResourceType = 'tickets' | 'timeEntries' | 'knowledgeBase' | 'reports' | 'settings' | 'admin' | 'companies';

const ROLE_PERMISSIONS: Record<CompanyMemberRole, Record<ResourceType, PermissionAction[]>> = {
  owner: {
    tickets: ['view', 'create', 'edit', 'delete', 'assign', 'close', 'viewInternal', 'manage'],
    timeEntries: ['view', 'create', 'edit', 'delete', 'manage'],
    knowledgeBase: ['view', 'create', 'edit', 'delete', 'manage'],
    reports: ['view', 'manage'],
    settings: ['view', 'edit', 'manage'],
    admin: ['view', 'manage'],
    companies: ['view', 'edit', 'manage'],
  },
  admin: {
    tickets: ['view', 'create', 'edit', 'delete', 'assign', 'close', 'viewInternal', 'manage'],
    timeEntries: ['view', 'create', 'edit', 'delete', 'manage'],
    knowledgeBase: ['view', 'create', 'edit', 'delete', 'manage'],
    reports: ['view', 'manage'],
    settings: ['view', 'edit', 'manage'],
    admin: [],
    companies: ['view', 'edit'],
  },
  technician: {
    tickets: ['view', 'create', 'edit', 'assign', 'close', 'viewInternal'],
    timeEntries: ['view', 'create', 'edit', 'delete'],
    knowledgeBase: ['view', 'create', 'edit'],
    reports: ['view'],
    settings: ['view'],
    admin: [],
    companies: ['view'],
  },
  customer: {
    tickets: ['view', 'create'],
    timeEntries: [],
    knowledgeBase: ['view'],
    reports: [],
    settings: [],
    admin: [],
    companies: ['view'],
  },
};

interface PermissionsContextValue {
  role: CompanyMemberRole | null;
  can: (action: PermissionAction, resource: ResourceType) => boolean;
  isStaff: boolean;
  isAdmin: boolean;
  isPortalUser: boolean;
  isPortalOnly: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { currentCompany } = useCompany();

  const role = useMemo<CompanyMemberRole | null>(() => {
    if (!user) return null;
    // Super admins are always 'owner' — decide this BEFORE the company guard so
    // a not-yet-resolved currentCompany can't momentarily make them non-staff
    // (which was bouncing them to the customer portal).
    if ((user as any).isSuperAdmin) return 'owner';
    if (!currentCompany) return null;
    const userId = (user as any).id ?? (user as any)._id;
    const companyId = (currentCompany as any).id ?? (currentCompany as any).uuid;
    const members = (user as any).companyMembers ?? currentCompany.members ?? [];
    const member = members.find(
      (m: any) =>
        (m.companyId === companyId || (m.company?.id ?? m.company?.uuid) === companyId) &&
        (m.userId === userId || (m.user?.id ?? m.user?._id) === userId) &&
        (m.isActive !== false)
    );
    if (member) return (member.role as CompanyMemberRole) || null;
    const companyMember = currentCompany.members?.find(
      (m: any) => (typeof m.user === 'string' ? m.user : (m.user as any)?.id ?? (m.user as any)?._id) === userId && m.isActive
    );
    return (companyMember?.role as CompanyMemberRole) || null;
  }, [user, currentCompany]);

  const can = useMemo(() => {
    return (action: PermissionAction, resource: ResourceType): boolean => {
      if (!role) return false;
      if (user?.isSuperAdmin) return true;
      return ROLE_PERMISSIONS[role]?.[resource]?.includes(action) ?? false;
    };
  }, [role, user]);

  const isStaff = role !== null && role !== 'customer';
  const isAdmin = role === 'owner' || role === 'admin';
  const isPortalUser = role === 'customer';

  // Check if user has ONLY customer roles across ALL companies
  const isPortalOnly = useMemo(() => {
    if (!user) return false;
    if ((user as any).isSuperAdmin) return false;
    const members = (user as any).companyMembers ?? [];
    if (members.length === 0) return true; // no companies = portal-only (needs to join)
    return members.every((m: any) => m.role === 'customer' && m.isActive !== false);
  }, [user]);

  return (
    <PermissionsContext.Provider value={{ role, can, isStaff, isAdmin, isPortalUser, isPortalOnly }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider');
  return ctx;
}
